import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  notification,
  Popover,
  Row,
  Select,
  Switch,
} from "antd";
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { arrayRemove, arrayUnion, deleteField, doc, getDoc, updateDoc } from "firebase/firestore";
import styled from "styled-components";
import axios from "axios";
import { cloneDeep, isEqual } from "lodash";
import QuantityControl from "components/shared-components/QuantityControl";
import { useProducts } from "../../../../../data/hooks/firestore/useProducts";

// Constants
import {
  ADDITIONAL_DELIVERY_LABEL,
  DEFAULT_DELIVERY_LABEL_FALLBACK,
  DEFAULT_DELIVERY_TIME,
  MORNING_DELIVERY_CHARGE,
  MORNING_DELIVERY_TIME,
  MORNING_EXTRA_CHARGE_LABELS,
  ORDER_PICKING_STATUS_IN_PROGRESS,
  ORDER_STATUS_CANCELED,
} from "../../../../../constants/OrderConstant";
import {
  FIRESTORE_ORDERPICKINGS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE, GROWY } from "../../../../../constants/CompanyConstants";
import { CURRENCY, FREE_DELIVERY_FROM_PRICE } from "../../../../../constants/DeliveryConstants";
import { INVOICE_SURCHARGE_PERCENTAGE, JIMMY_LOVES_FOOD_ID } from "../../../../../constants/PricingConstants";
import { CRATES } from "../../../../../constants/PackagingConstants";

// Functions
import { formatPrice } from "../../../../../functions/formatters";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { generateAndEmailDocuments, sooCoolOrder, stockApiUrl } from "../../../../../urls";
import {
  applyCustomerPricingToProduct,
  calculateOrderShippingCost,
  getBundleItemsWithCustomerPricing,
  getBundlePackagingPrice,
  recalculateBundleItemPricesForOverride,
} from "../../../../../functions/pricing";
import { PAYMENT_METHOD_INVOICE } from "../../../../../constants/PaymentConstants";
import dayjs from "dayjs";
import {
  calculateOrderUpdatePricing,
  handleSooCoolOrderChanges,
  prepareShoppingCartUpdate,
  sendOrderUpdateNotification,
  updateOrderPickingForEditedOrder,
} from "../../../../../functions/orders";
import { getPickingDate } from "../../../../../functions/deliveryValidation";
import { updatePackagingStockDelta } from "../../../../../functions/packagingStock";
import { useStockData } from "../../../../../data/hooks/firestore/useStock";
import { usePriceLists } from "../../../../../data/hooks/firestore/usePriceLists";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";
import { validateDeliveryDay as validateDeliveryDayUtil } from "../../../../../functions/deliveryValidation";

const EditOrderModal = ({
  visible,
  onCancel,
  order,
  customers,
  users,
  products,
  orders,
  firestore,
  handleSendToSooCool,
  handleShippingLabelsPrint,
  setIsPrinting,
  setShippingLabelsOrders,
}) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [editOrderForm] = Form.useForm();

  // State
  const [updatedOrderProducts, setUpdatedOrderProducts] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [crateQuantity, setCrateQuantity] = useState(null);
  const [calculatedCrateQuantity, setCalculatedCrateQuantity] = useState(null);
  const [scannedCrates, setScannedCrates] = useState([]);
  const [addCrateDropdownVisible, setAddCrateDropdownVisible] = useState(false);
  const [editAddShippingCost, setEditAddShippingCost] = useState(true);
  const [editNewProduct, setEditNewProduct] = useState("");
  const [tempDeliveryDate, setTempDeliveryDate] = useState(null);
  const [productsExceedingStock, setProductsExceedingStock] = useState({});
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [deliveryLabel, setDeliveryLabel] = useState(DEFAULT_DELIVERY_LABEL_FALLBACK);
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState(null);
  const [orderBeingPicked, setOrderBeingPicked] = useState(false);
  const [orderWasBeingPicked, setOrderWasBeingPicked] = useState(false);
  const [pickingUserEmail, setPickingUserEmail] = useState("");
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [manualPriceOverrides, setManualPriceOverrides] = useState({});
  const [mainOrder, setMainOrder] = useState(null);
  const [deliveryDayWarning, setDeliveryDayWarning] = useState(null);
  const [currentDeliveryDate, setCurrentDeliveryDate] = useState(null);
  const { products: allProductsData } = useProducts();

  // Normalize timeslot to JSON with consistent key order to prevent display mismatches
  const stringifyTimeSlot = (slot) =>
    JSON.stringify({ start: slot.start, end: slot.end, deliverySurcharge: Number(slot.deliverySurcharge || 0) });

  const normalizeScannedCrates = (crates = []) => {
    return (Array.isArray(crates) ? crates : [])
      .map((crate) => ({
        barcode: crate?.barcode || "",
        size: crate?.size || "",
        quantity: Number(crate?.quantity || 0),
      }))
      .sort((a, b) => {
        const keyA = `${a.barcode}-${a.size}`;
        const keyB = `${b.barcode}-${b.size}`;
        return keyA.localeCompare(keyB);
      });
  };

  // Check if order has ADD delivery label
  const isAddDelivery = order?.deliveryLabel === ADDITIONAL_DELIVERY_LABEL;
  const productsToUse = showAllProducts && allProductsData ? allProductsData : products;
  const { stockData, stockCarryOverData } = useStockData();
  const { priceLists } = usePriceLists();
  const { getCountriesMap, getCountryDelivery, getCountryDeliveryLabels, getDeliveryLabelLeadDays } = useCountries();
  const defaultDeliverySettings = getCountryDelivery(order?.customer?.country || COMPANY_COUNTRY_CODE);

  // Delivery date -> packing dateId (DDMMYYYY), using the lead time
  // configured for the order's customer country + delivery label.
  const getEditOrderPackingDate = (deliveryDate) => {
    const leadDays = getDeliveryLabelLeadDays(order?.customer?.country || COMPANY_COUNTRY_CODE, deliveryLabel);
    return getPickingDate(deliveryDate, leadDays);
  };
  const getEditOrderPackingDateId = (deliveryDate) => {
    if (!deliveryDate) return null;
    return getEditOrderPackingDate(deliveryDate).format("DDMMYYYY");
  };

  // Helper to get invoice surcharge percentage for the customer
  const getInvoiceSurchargePercentage = (customer) => {
    if (!customer) return INVOICE_SURCHARGE_PERCENTAGE * 100;
    const countriesMap = getCountriesMap();
    const customerCountry = customer.country || "NL";
    const countrySettings = countriesMap?.[customerCountry]?.pricing;
    return countrySettings?.invoiceSurchargePercentage ?? INVOICE_SURCHARGE_PERCENTAGE * 100;
  };

  // Helpers for stock logic
  const isStockCarriedOver = (packingDateId) => stockCarryOverData[packingDateId] === true;

  const getStockInfo = (packingDateId, productId, source = stockData) => {
    return (
      source?.[packingDateId]?.[productId] || {
        currentStock: 0,
        projectedStock: 0,
        requestedStock: 0,
      }
    );
  };

  const getAvailableStock = (stockInfo, carriedOver) => {
    return carriedOver
      ? stockInfo.currentStock || 0
      : (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
  };

  // Change reason modal voor producten
  const [currentSessionToggles, setCurrentSessionToggles] = useState({});

  // Add ref to track initialization
  const hasInitialized = useRef(false);

  const dateValue = Form.useWatch("deliveryDate", editOrderForm);

  // Computed values
  const editOrderSubtotal = useMemo(() => {
    if (!updatedOrderProducts || updatedOrderProducts.length === 0) return 0;

    return updatedOrderProducts.reduce((acc, item) => {
      const price = manualPriceOverrides[item.productId]
        ? parseFloat(manualPriceOverrides[item.productId])
        : parseFloat(item.productPrice);
      return acc + price * item.itemCount;
    }, 0);
  }, [updatedOrderProducts, manualPriceOverrides]);

  const editOrderCalculatedShippingCost = useMemo(() => {
    if (!order) return 0;
    return calculateOrderShippingCost(
      editOrderSubtotal,
      order.farmorderCustomerId,
      customers,
      deliveryLabel,
      defaultDeliverySettings,
    );
  }, [editOrderSubtotal, order, customers, deliveryLabel, defaultDeliverySettings]);

  // Computed values for discount
  const discountCodeData = useMemo(
    () => ({
      applied: order?.discountCodeApplied || false,
      amount: order?.discountCodeAmount || 0,
      percentage: order?.discountCodePercentage || 0,
    }),
    [order],
  );

  const editOrderDiscountAmount = useMemo(() => {
    if (!discountCodeData.applied) return 0;

    if (discountCodeData.amount > 0) {
      return discountCodeData.amount;
    } else if (discountCodeData.percentage > 0) {
      return editOrderSubtotal * (discountCodeData.percentage / 100);
    }
    return 0;
  }, [discountCodeData.applied, discountCodeData.amount, discountCodeData.percentage, editOrderSubtotal]);

  const getDiscountDisplayText = () => {
    if (discountCodeData.percentage > 0) {
      return `${t("discount_code_applied")} (${discountCodeData.percentage}%)`;
    } else if (discountCodeData.amount > 0) {
      return `${t("discount_code_applied")} (${CURRENCY}${formatPrice(discountCodeData.amount)})`;
    }
    return t("discount_code_applied");
  };

  // Function to reset all modal-related state
  const resetModalState = () => {
    setUpdatedOrderProducts(null);
    setCrateQuantity(null);
    setCalculatedCrateQuantity(null);
    setScannedCrates([]);
    setAddCrateDropdownVisible(false);
    setEditAddShippingCost(true);
    setDeliveryLabel(defaultDeliverySettings?.defaultLabel || DEFAULT_DELIVERY_LABEL_FALLBACK);
    setDeliveryTimeSlot(null);
    setSubmitLoading(false);
    setEditNewProduct("");
    setTempDeliveryDate(null);
    setProductsExceedingStock({});
    setDatePickerVisible(false);
    setShowAllProducts(false);
    setManualPriceOverrides({});
    setOrderWasBeingPicked(false);
    setDeliveryDayWarning(null);
    setCurrentDeliveryDate(null);
  };

  // Initialize modal when it opens - ONLY ONCE
  useEffect(() => {
    if (visible && order && !hasInitialized.current) {
      // Check if order is being picked
      checkIfOrderBeingPicked(order.id);

      // Reset state before setting new values
      resetModalState();

      const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);

      // Calculate if order actually has shipping costs
      const hasShippingCosts = order.shippingCostDisabled === true ? false : parseFloat(order.shippingCost || 0) > 0;

      // Parse delivery time from order if available
      // Only populate if order has deliveryTimeSlot object (new orders)
      // Old orders only have deliveryTime string, so leave field empty
      // ADD orders never have timeslots
      let timeSlotValue = null;
      if (order.deliveryLabel === ADDITIONAL_DELIVERY_LABEL) {
        // ADD orders never have timeslots
        timeSlotValue = null;
        setDeliveryTimeSlot(null);
      } else if (order.deliveryTimeSlot) {
        // New order with timeslot object - validate it still exists in country settings
        const companyDeliverySettings = getCountryDelivery(dbCustomer?.country || COMPANY_COUNTRY_CODE);
        const labelConfig = companyDeliverySettings?.deliveryLabels?.find(
          (dl) => dl.deliveryLabel === order.deliveryLabel,
        );
        const availableTimeSlots = labelConfig?.deliveryTimeSlots || [];

        // Check if the order's timeslot still exists in the current settings
        const timeslotStillExists = availableTimeSlots.some(
          (slot) =>
            slot.start === order.deliveryTimeSlot.start &&
            slot.end === order.deliveryTimeSlot.end &&
            Number(slot.deliverySurcharge || 0) === Number(order.deliveryTimeSlot.deliverySurcharge || 0),
        );

        if (timeslotStillExists) {
          // Timeslot still exists, set it
          timeSlotValue = stringifyTimeSlot(order.deliveryTimeSlot);
          setDeliveryTimeSlot(order.deliveryTimeSlot);
        } else {
          // Timeslot was deleted from settings, leave field empty
          timeSlotValue = null;
          setDeliveryTimeSlot(null);
        }
      } else if (order.deliveryTime && order.deliveryTime.includes("-")) {
        // Old order - don't populate the field, but keep the state for display purposes
        const timeSlot = {
          start: order.deliveryTime.split("-")[0]?.trim(),
          end: order.deliveryTime.split("-")[1]?.trim(),
          deliverySurcharge: order.deliveryTimeCharge || 0,
        };
        setDeliveryTimeSlot(timeSlot);
        // Don't set timeSlotValue - leave the form field empty for old orders
      }

      editOrderForm.setFieldsValue({
        deliveryDate: dayjs(order.farmorderDeliveryDate),
        deliveryLabel: order.deliveryLabel ? order.deliveryLabel : dbCustomer?.deliveryLabel,
        deliveryTimeSlot: timeSlotValue,
        addShippingCost: hasShippingCosts,
        internalNotes: order.internalNotes ? order.internalNotes.text : null,
      });

      if (order.farmorderComments) {
        editOrderForm.setFieldsValue({
          orderComments: order.farmorderComments,
          orderCommentsProcessed: order.orderCommentsProcessed,
        });
      }

      setUpdatedOrderProducts(order.userShoppingCart);
      setCurrentSessionToggles({});
      if (order.crates) setCrateQuantity(order.crates);
      if (order.scannedCrates) setScannedCrates(order.scannedCrates);
      setEditAddShippingCost(hasShippingCosts);

      setDeliveryLabel(order.deliveryLabel || defaultDeliverySettings?.defaultLabel || DEFAULT_DELIVERY_LABEL_FALLBACK);
      setCurrentDeliveryDate(dayjs(order.farmorderDeliveryDate));

      // If this is an additional delivery, fetch the main order
      if (order.deliveryLabel === ADDITIONAL_DELIVERY_LABEL && order.mainOrderId) {
        const mainOrderData = orders.find((o) => o.id === order.mainOrderId);
        setMainOrder(mainOrderData);
      }

      // Mark as initialized
      hasInitialized.current = true;
    }

    // Reset initialization flag when modal closes
    if (!visible) {
      hasInitialized.current = false;
      setCurrentSessionToggles({});
    }
  }, [visible, order]);

  // Calculate crate quantity based on scannedCrates or fallback to calculated
  useEffect(() => {
    if (scannedCrates.length > 0) {
      const totalCrates = scannedCrates.reduce((sum, crate) => sum + crate.quantity, 0);
      setCrateQuantity(totalCrates);
    } else if (updatedOrderProducts?.length > 0) {
      setCalculatedCrateQuantity(1);
      if (crateQuantity === null) {
        setCrateQuantity(1);
      }
    } else {
      setCalculatedCrateQuantity(null);
    }
  }, [updatedOrderProducts, scannedCrates]);

  // No shipping costs for JLVO orders
  useEffect(() => {
    if (deliveryLabel === "JLVO") {
      setEditAddShippingCost(false);
      editOrderForm.setFieldsValue({ addShippingCost: false });
    }
  }, [deliveryLabel, editOrderForm]);

  // Clear and disable timeslot for ADD orders
  useEffect(() => {
    if (deliveryLabel === ADDITIONAL_DELIVERY_LABEL) {
      editOrderForm.setFieldsValue({ deliveryTimeSlot: null });
      setDeliveryTimeSlot(null);
    }
  }, [deliveryLabel, editOrderForm]);

  // Validate delivery day when date, label, or customer changes
  useEffect(() => {
    if (currentDeliveryDate && order && deliveryLabel) {
      const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
      if (dbCustomer) {
        const warning = validateDeliveryDay(currentDeliveryDate, dbCustomer, deliveryLabel);
        setDeliveryDayWarning(warning);
      }
    } else {
      setDeliveryDayWarning(null);
    }
  }, [currentDeliveryDate, deliveryLabel, order, customers]);

  // Initial stock validation when modal opens
  useEffect(() => {
    if (visible && order && updatedOrderProducts && stockData) {
      const deliveryDate = editOrderForm.getFieldValue("deliveryDate") || order.farmorderDeliveryDate;
      const packingDateId = getEditOrderPackingDateId(deliveryDate);
      const latestStockData = stockData[packingDateId] || {};
      const stockCarriedOver = isStockCarriedOver(packingDateId);

      const exceedingProducts = {};

      updatedOrderProducts.forEach((product) => {
        let originalQuantity = 0;
        if (order && order.userShoppingCart) {
          const originalProduct = order.userShoppingCart.find((item) => item.productId === product.productId);
          if (originalProduct) {
            const originalPackingDateId = getEditOrderPackingDateId(order.farmorderDeliveryDate);
            if (packingDateId === originalPackingDateId) {
              originalQuantity = originalProduct.itemCount;
            }
          }
        }

        const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: latestStockData });
        const baseAvailable = getAvailableStock(stockInfo, stockCarriedOver);
        const effectiveStock = baseAvailable + originalQuantity;

        if (product.itemCount > effectiveStock) {
          exceedingProducts[product.productId] = true;
        }
      });

      setProductsExceedingStock(exceedingProducts);
    }
  }, [visible, order, updatedOrderProducts, stockData, stockCarryOverData, editOrderForm, dateValue]);

  const checkIfOrderBeingPicked = async (orderId) => {
    try {
      const orderPickingDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERPICKINGS_TABLE,
        orderId,
      );
      const orderPickingDoc = await getDoc(orderPickingDocRef);

      if (orderPickingDoc.exists()) {
        const data = orderPickingDoc.data();
        if (data.status === ORDER_PICKING_STATUS_IN_PROGRESS) {
          setOrderWasBeingPicked(true);
          setPickingUserEmail(data.userEmail);

          // Scenario 1: Actively picking (isPaused = false)
          if (data.isPaused === false) {
            setOrderBeingPicked(true);
            showActivePickingModal(data.userEmail, orderId, () => {
              // User confirmed override - modal stays open
              setOrderBeingPicked(false); // Allow editing without further warnings
            });
            return true;
          }

          // Scenario 2: Paused (isPaused = true)
          if (data.isPaused === true) {
            setOrderBeingPicked(false);
            showPausedPickingModal(() => {
              // User acknowledged the paused state - modal stays open
            });
            return false; // Not actively being picked, allow editing
          }
        }
      }
      setOrderBeingPicked(false);
      setOrderWasBeingPicked(false);
      setPickingUserEmail("");
      return false; // Order is not being picked
    } catch (error) {
      console.error("Error checking order picking status:", error);
      return false;
    }
  };

  const showActivePickingModal = (userEmail, orderId, onConfirm) => {
    Modal.confirm({
      title: t("order_being_actively_picked"),
      content: (
        <div>
          <p style={{ fontWeight: "bold" }}>{t("order_currently_being_picked_by", { email: userEmail })}</p>
          <p style={{}}>{t("active_picking_warning_title")}</p>
          <ul style={{ marginTop: "8px" }}>
            <li>{t("active_picking_warning_1")}</li>
            <li>{t("active_picking_warning_3")}</li>
            <li>{t("active_picking_warning_4")}</li>
          </ul>
          <p style={{ marginTop: "12px", fontWeight: "bold" }}>{t("only_proceed_if_emergency")}</p>
        </div>
      ),
      okText: t("override_emergency"),
      cancelText: t("cancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          // Update isPaused to false when override is confirmed
          const docRef = doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_ORDERPICKINGS_TABLE,
            orderId,
          );
          await updateDoc(docRef, {
            isPaused: true,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          });
          onConfirm();
        } catch (error) {
          console.error("Error updating isPaused flag:", error);
          message.error(t("error_updating_order_picking_status"));
        }
      },
      onCancel: () => {
        onCancel(); // Close the edit modal
      },
    });
  };

  const showPausedPickingModal = (onConfirm) => {
    Modal.info({
      title: t("order_picking_paused"),
      content: (
        <div>
          <p>{t("order_picking_is_paused_info")}</p>
          <p style={{ marginTop: "12px", fontWeight: "bold" }}>{t("paused_picking_warning")}</p>
          <p style={{ marginTop: "8px" }}>{t("wait_until_editing_complete")}</p>
        </div>
      ),
      okText: t("ok_i_understand"),
      onOk: onConfirm,
    });
  };

  // Validate delivery day against customer and delivery label settings
  const validateDeliveryDay = (date, customer, label) => {
    return validateDeliveryDayUtil(date, customer, label, getCountryDelivery, COMPANY_COUNTRY_CODE, t);
  };

  const validateAndUpdateDate = async (date) => {
    if (!date || !updatedOrderProducts || updatedOrderProducts.length === 0) {
      setDatePickerVisible(false);
      return;
    }

    // Update current delivery date state to trigger validation
    setCurrentDeliveryDate(date);

    try {
      const packingDateId = getEditOrderPackingDateId(date);
      const newStockData = stockData[packingDateId] || {};
      const stockCarriedOver = isStockCarriedOver(packingDateId);

      const unavailableProducts = [];
      for (const product of updatedOrderProducts) {
        let originalQuantity = 0;
        if (order && order.userShoppingCart) {
          const originalProduct = order.userShoppingCart.find((item) => item.productId === product.productId);
          if (originalProduct) {
            originalQuantity = originalProduct.itemCount;
          }
        }

        const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: newStockData });

        const originalPackingDateId = getEditOrderPackingDateId(order?.farmorderDeliveryDate);
        const shouldAddOriginalQuantity = packingDateId === originalPackingDateId;

        const baseAvailable = getAvailableStock(stockInfo, stockCarriedOver);
        const effectiveStock = baseAvailable + (shouldAddOriginalQuantity ? originalQuantity : 0);

        if (product.itemCount > effectiveStock) {
          unavailableProducts.push({
            name: product.productName,
            ordered: product.itemCount,
            available: effectiveStock,
          });
        }
      }

      if (unavailableProducts.length > 0) {
        setDatePickerVisible(false);

        Modal.confirm({
          title: t("notifications.stock_issues_for_selected_date"),
          content: (
            <div>
              <p>{t("notifications.insufficient_stock_for_date")}</p>
              <ul style={{ margin: "0 0 20px 20px", padding: 0 }}>
                {unavailableProducts.map((product, index) => (
                  <li key={index}>
                    {product.name}: {t("ordered")} {product.ordered}, {t("available")} {product.available}
                  </li>
                ))}
              </ul>
              <p>{t("proceed_anyway_negative_stock_warning")}</p>
            </div>
          ),
          zIndex: 1100,
          okText: t("proceed_anyway"),
          cancelText: t("cancel"),
          onOk: () => {
            editOrderForm.setFieldsValue({ deliveryDate: date });
            setTempDeliveryDate(date);
          },
          onCancel: () => {
            editOrderForm.setFieldsValue({ deliveryDate: tempDeliveryDate });
          },
        });
      } else {
        editOrderForm.setFieldsValue({ deliveryDate: date });
        setTempDeliveryDate(date);
        setDatePickerVisible(false);
      }
    } catch (error) {
      console.error("Error checking stock for new date:", error);
      message.error(t("notifications.failed_check_stock"));
      setDatePickerVisible(false);
    }
  };

  const validateStockForOrderUpdate = (updatedOrderProducts, originalOrder, stockData, deliveryDate) => {
    const stockErrors = [];
    const exceedingProducts = {};

    const packingDateId = getEditOrderPackingDateId(deliveryDate);
    let latestStockData = stockData[packingDateId] || {};

    if (!latestStockData || Object.keys(latestStockData).length === 0) {
      console.warn(`No stock data found for packing date ${packingDateId}`);
      const alternateFormat = getEditOrderPackingDate(deliveryDate).format("DD-MM-YYYY");
      if (stockData[alternateFormat]) {
        latestStockData = stockData[alternateFormat];
      }
    }

    const stockCarriedOver = isStockCarriedOver(packingDateId);

    for (const product of updatedOrderProducts) {
      let originalQuantity = 0;
      if (originalOrder && originalOrder.userShoppingCart) {
        const originalProduct = originalOrder.userShoppingCart.find((item) => item.productId === product.productId);
        if (originalProduct) {
          const originalPackingDateId = getEditOrderPackingDateId(originalOrder.farmorderDeliveryDate);
          const isSamePackingDate = packingDateId === originalPackingDateId;
          if (isSamePackingDate) {
            originalQuantity = originalProduct.itemCount;
          }
        }
      }

      const stockInfo = getStockInfo(packingDateId, product.productId, { [packingDateId]: latestStockData });
      const originalPackingDateId = getEditOrderPackingDateId(originalOrder.farmorderDeliveryDate);
      const isSamePackingDate = packingDateId === originalPackingDateId;
      const baseAvailable = getAvailableStock(stockInfo, stockCarriedOver);
      const effectiveStock = baseAvailable + (isSamePackingDate ? originalQuantity : 0);

      if (product.itemCount > effectiveStock) {
        stockErrors.push({
          name: product.productName,
          ordered: product.itemCount,
          available: effectiveStock,
        });
        exceedingProducts[product.productId] = true;
      }
    }

    setProductsExceedingStock(exceedingProducts);
    return stockErrors;
  };

  const cancelOrderInSooCool = async (sooCoolId, orderId) => {
    if (!sooCoolId) return;
    try {
      const token = await getFirebaseIDToken();
      await axios.delete(sooCoolOrder + `/${sooCoolId}`, { headers: { Authorization: token } }).catch((err) => {
        throw new Error(err.response.data.message);
      });

      const orderDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERS_TABLE,
        orderId,
      );
      await updateDoc(orderDocRef, {
        oldSooCoolId: sooCoolId,
        sooCoolId: null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      message.success(t("notifications.order_cancelled_soo_cool"));
    } catch (error) {
      console.error("Error canceling order:", error);
      message.error(t("notifications.error_canceling_order_soo_cool"));
    }
  };

  const decreaseQuantity = (product) => {
    const index = updatedOrderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(updatedOrderProducts);

    if (index !== -1) {
      newProducts[index].itemCount -= 1;

      if (productsExceedingStock[product.productId]) {
        const packingDateId = getEditOrderPackingDateId(editOrderForm.getFieldValue("deliveryDate"));
        const stockCarriedOver = isStockCarriedOver(packingDateId);
        const stockInfo = getStockInfo(packingDateId, product.productId, stockData);
        let baseAvailable = getAvailableStock(stockInfo, stockCarriedOver);

        if (order && order.userShoppingCart) {
          const originalProduct = order.userShoppingCart.find((item) => item.productId === product.productId);
          if (originalProduct) {
            const originalPackingDateId = getEditOrderPackingDateId(order.farmorderDeliveryDate);
            if (packingDateId === originalPackingDateId) {
              baseAvailable += originalProduct.itemCount;
            }
          }
        }

        if (newProducts[index].itemCount <= baseAvailable) {
          setProductsExceedingStock((prev) => {
            const newState = { ...prev };
            delete newState[product.productId];
            return newState;
          });
        }
      }

      // Check if product existed in original order
      const existedInOriginalOrder = order.userShoppingCart.some((item) => item.productId === product.productId);

      // Remove newly added products when reaching 0, but keep existing products
      if (newProducts[index].itemCount === 0 && !existedInOriginalOrder) {
        newProducts.splice(index, 1);
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    }

    setUpdatedOrderProducts(newProducts);
  };

  const increaseQuantity = (product) => {
    const index = updatedOrderProducts.findIndex((item) => item.productId === product.productId);
    const newProducts = cloneDeep(updatedOrderProducts);
    const packingDateId = getEditOrderPackingDateId(editOrderForm.getFieldValue("deliveryDate"));
    const stockCarriedOver = isStockCarriedOver(packingDateId);
    const stockInfo = getStockInfo(packingDateId, product.productId, stockData);

    let originalQuantity = 0;
    if (order && order.userShoppingCart) {
      const originalProduct = order.userShoppingCart.find((item) => item.productId === product.productId);
      if (originalProduct) {
        const originalPackingDateId = getEditOrderPackingDateId(order.farmorderDeliveryDate);
        if (packingDateId === originalPackingDateId) {
          originalQuantity = originalProduct.itemCount;
        }
      }
    }

    const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);

    if (index !== -1) {
      newProducts[index].itemCount += 1;

      const effectiveStock = getAvailableStock(stockInfo, stockCarriedOver) + originalQuantity;

      if (newProducts[index].itemCount > effectiveStock) {
        setProductsExceedingStock((prev) => ({
          ...prev,
          [product.productId]: true,
        }));
      } else {
        setProductsExceedingStock((prev) => {
          const newState = { ...prev };
          delete newState[product.productId];
          return newState;
        });
      }
    } else {
      const effectiveStock = getAvailableStock(stockInfo, stockCarriedOver) + originalQuantity;

      if (effectiveStock <= 0) {
        message.warning(t("notifications.product_exceeds_available_stock"));
      }

      const finalPrice = applyCustomerPricingToProduct(product, dbCustomer, priceLists, getCountriesMap());

      // Calculate bundle item prices for reporting purposes only
      // Note: The bundle price (finalPrice) is NEVER recalculated - it's a fixed price set via BundlePriceCalculator
      let bundleItemsWithPricing = [];
      let packagingMaterialWithPricing = null;

      if (product.isBundle && product.bundleItems) {
        const allItemsWithPricing = getBundleItemsWithCustomerPricing(
          product,
          dbCustomer,
          finalPrice,
          products,
          priceLists,
          getCountriesMap(),
        );

        // Separate packaging items from regular bundle items
        bundleItemsWithPricing = allItemsWithPricing.filter((item) => !item.isPackaging);
        const packagingItem = allItemsWithPricing.find((item) => item.isPackaging);

        // Packaging keeps original price (no invoice surcharge or discount)
        if (packagingItem) {
          packagingMaterialWithPricing = packagingItem;
        } else if (product.packagingMaterial) {
          packagingMaterialWithPricing = product.packagingMaterial;
        }
      }

      newProducts.push({
        externalId: product.exactId?.toString() || "",
        itemCount: 1,
        orderable: true,
        productCategory: product.category?.en || "",
        productId: product.productId,
        productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
        productName: product.productName,
        productPrice: finalPrice,
        productBarcode: product.barcode || "",
        // Store bundle pricing data if this is a bundle
        ...(product.isBundle && {
          isBundle: true,
          bundleItems: bundleItemsWithPricing,
          packagingMaterial: packagingMaterialWithPricing,
          hasCustomPrice: product.hasCustomPrice || false,
        }),
      });

      if (effectiveStock <= 0) {
        setProductsExceedingStock((prev) => ({
          ...prev,
          [product.productId]: true,
        }));
      }
    }

    setUpdatedOrderProducts(newProducts);
  };

  const hasQuantityChanged = (productId, currentQuantity, order) => {
    const originalProduct = order.userShoppingCart.find((item) => item.productId === productId);

    if (!originalProduct) {
      // New product added
      return currentQuantity > 0;
    }

    // Existing product - check if quantity changed
    return originalProduct.itemCount !== currentQuantity;
  };

  const decreaseCrateQuantity = () => {
    if (crateQuantity === null && calculatedCrateQuantity > 1) {
      setCrateQuantity(calculatedCrateQuantity - 1);
    } else if (crateQuantity > 1) {
      setCrateQuantity(crateQuantity - 1);
    }
  };

  const increaseCrateQuantity = () => {
    if (crateQuantity === null) {
      setCrateQuantity(calculatedCrateQuantity + 1);
    } else {
      setCrateQuantity(crateQuantity + 1);
    }
  };

  const handleAddCrate = (crateBarcode) => {
    const crate = CRATES.find((c) => c.barcode === crateBarcode);
    if (!crate) return;

    setScannedCrates((prev) => {
      const existing = prev.find((c) => c.barcode === crateBarcode);
      if (existing) {
        return prev.map((c) => (c.barcode === crateBarcode ? { ...c, quantity: c.quantity + 1 } : c));
      } else {
        return [...prev, { barcode: crateBarcode, quantity: 1, size: crate.size }];
      }
    });
  };

  const getQuantityDifference = (productId, currentQuantity) => {
    if (!order || !order.userShoppingCart) return null;

    const originalProduct = order.userShoppingCart.find((item) => item.productId === productId);
    if (!originalProduct) return `+${currentQuantity}`;

    const diff = currentQuantity - originalProduct.itemCount;
    if (diff === 0) return null;
    return diff > 0 ? `+${diff}` : diff;
  };

  const handlePrint = async (updatedOrderData = null) => {
    const token = await getFirebaseIDToken();
    const dbOrder = updatedOrderData || orders.find((o) => o.id === order.id);

    try {
      // Send async request to generate and email documents
      await axios.post(generateAndEmailDocuments, { orderId: dbOrder.id }, { headers: { Authorization: token } });

      message.success(t("notifications.documents_will_be_emailed"));
    } catch (error) {
      console.error("Failed to send print emails:", error);
      message.error(t("notifications.error_sending_print_emails"));
    }

    onCancel();
  };

  const showOperationSummary = (results) => {
    const successfulOps = [];
    const failedOps = [];
    const partialOps = [];

    Object.entries(results).forEach(([operation, result]) => {
      if (!result.attempted) return;

      if (result.success && !result.error) {
        successfulOps.push(operation);
      } else if (result.success && result.error) {
        partialOps.push({ operation, error: result.error });
      } else {
        failedOps.push({ operation, error: result.error });
      }
    });

    const hasFailures = failedOps.length > 0;
    const hasPartial = partialOps.length > 0;

    const title = hasFailures
      ? t("notifications.order_update_partial")
      : hasPartial
        ? t("notifications.order_update_with_warnings")
        : t("notifications.order_update_complete");

    const content = (
      <div>
        {successfulOps.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#52c41a" }}>{t("successful_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {successfulOps.map((op) => (
                <li key={op}>{getOperationDisplayName(op)}</li>
              ))}
            </ul>
          </div>
        )}

        {partialOps.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <strong style={{ color: "#faad14" }}>{t("partial_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {partialOps.map(({ operation, error }) => (
                <li key={operation}>
                  {getOperationDisplayName(operation)}: {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {failedOps.length > 0 && (
          <div>
            <strong style={{ color: "#ff4d4f" }}>{t("failed_operations")}:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>
              {failedOps.map(({ operation, error }) => (
                <li key={operation}>
                  {getOperationDisplayName(operation)}: {error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );

    notification.destroy();

    notification[hasFailures ? "error" : hasPartial ? "warning" : "success"]({
      message: title,
      description: content,
      duration: 0,
      style: { width: "400px" },
    });
  };

  const getOperationDisplayName = (operation) => {
    const names = {
      sooCool: t("order_updated_soocool"),
      firestore: t("order_updated_database"),
      stock: t("stock_updated"),
      email: t("email_sent"),
    };
    return names[operation] || operation;
  };

  const handleEditOrder = async (printLabelWhenDone, bypassCheck = false) => {
    if (orderBeingPicked && !bypassCheck) {
      showActivePickingModal(pickingUserEmail, () => {
        handleEditOrder(printLabelWhenDone, true);
      });
      return;
    }

    try {
      const values = await editOrderForm.validateFields();

      setSubmitLoading(true);

      const stockErrors = validateStockForOrderUpdate(updatedOrderProducts, order, stockData, values.deliveryDate);
      if (stockErrors.length > 0 && bypassCheck !== true) {
        Modal.confirm({
          title: t("notifications.stock_validation_failed"),
          content: (
            <div>
              <p>{t("notifications.products_exceed_stock")}</p>
              <ul>
                {stockErrors.map((error, index) => (
                  <li key={index}>
                    {error.name}: {t("ordered")} {error.ordered}, {t("available")} {error.available}
                  </li>
                ))}
              </ul>
              <p>{t("proceed_anyway_negative_stock_warning")}</p>
            </div>
          ),
          okText: t("proceed_anyway"),
          cancelText: t("cancel"),
          onOk: () => {
            const proceedWithEdit = () => handleEditOrder(printLabelWhenDone, true);
            proceedWithEdit();
          },
          onCancel: () => {
            setSubmitLoading(false);
          },
        });
        return;
      }

      const operationResults = {
        sooCool: { attempted: false, success: false, error: null },
        firestore: { attempted: true, success: false, error: null },
        stock: { attempted: false, success: false, error: null },
        email: { attempted: false, success: false, error: null },
      };

      // Apply current session toggles AND manual prices to products before saving
      const productsWithToggles = updatedOrderProducts.map((product) => {
        const toggle = currentSessionToggles[product.productId];

        // Apply manual price if set
        const overridePrice = manualPriceOverrides[product.productId];
        const finalProduct = {
          ...product,
          productPrice: overridePrice ? parseFloat(overridePrice).toFixed(2) : product.productPrice,
          ...(product.isBundle &&
            overridePrice && {
              bundleItems: recalculateBundleItemPricesForOverride(product, overridePrice),
            }),
        };

        if (toggle) {
          // Add changeRequestedBy from toggle
          return {
            ...finalProduct,
            changeRequestedBy: toggle.changeRequestedBy,
          };
        } else {
          // Remove any existing changeRequestedBy (error-driven)
          const { changeRequestedBy, changeReason, ...cleanProduct } = finalProduct;
          return cleanProduct;
        }
      });

      const newCrates = !!crateQuantity ? crateQuantity : calculatedCrateQuantity;
      const shippingCostSwitchChanged =
        order.shippingCostDisabled === undefined || order.shippingCostDisabled !== !editAddShippingCost;
      const orderCommentsSwitchChanged =
        !!order.farmorderComments &&
        (order.orderCommentsProcessed === undefined || order.orderCommentsProcessed !== values.orderCommentsProcessed);
      const internalNotesChanged = order.internalNotes?.text !== values.internalNotes;
      const deliveryLabelChanged = order.deliveryLabel !== values.deliveryLabel;
      const deliveryDateChanged = !isEqual(
        order.farmorderDeliveryDate,
        dayjs(values.deliveryDate).format("YYYY-MM-DD"),
      );
      const originalCrates = order.crates || 1;
      const cratesChanged = originalCrates !== newCrates;
      const scannedCratesChanged = !isEqual(
        normalizeScannedCrates(order.scannedCrates),
        normalizeScannedCrates(scannedCrates),
      );

      // Bereken cart changes
      const cartUpdateResult = prepareShoppingCartUpdate(order, productsWithToggles, email, deliveryDateChanged);
      const shoppingCartChanged = cartUpdateResult.shoppingCartChanged;

      if (
        !shoppingCartChanged &&
        !deliveryDateChanged &&
        !cratesChanged &&
        !scannedCratesChanged &&
        !deliveryLabelChanged &&
        !shippingCostSwitchChanged &&
        !orderCommentsSwitchChanged &&
        !internalNotesChanged
      ) {
        message.error(t("notifications.order_nothing_changed"));
        setSubmitLoading(false);
        return;
      }

      const updateObject = {
        farmorderDeliveryDate: dayjs(values.deliveryDate).format("YYYY-MM-DD"),
        crates: newCrates,
        scannedCrates: scannedCrates || [],
      };

      const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
      updateObject.priceListId = dbCustomer?.priceListId || null;

      if (cartUpdateResult.hasChanges) {
        Object.assign(updateObject, cartUpdateResult.updateObject);
      }

      // Check if delivery time slot changed
      const deliveryTimeSlotChanged = (() => {
        const currentTimeSlotValue = values.deliveryTimeSlot;
        const originalTimeSlotValue =
          order.deliveryTime && order.deliveryTimeCharge !== undefined
            ? stringifyTimeSlot({
                start: order.deliveryTime.split("-")[0]?.trim(),
                end: order.deliveryTime.split("-")[1]?.trim(),
                deliverySurcharge: order.deliveryTimeCharge || 0,
              })
            : null;
        return currentTimeSlotValue !== originalTimeSlotValue;
      })();

      if (deliveryLabelChanged || deliveryTimeSlotChanged) {
        // ADD orders don't have timeslots
        if (values.deliveryLabel === ADDITIONAL_DELIVERY_LABEL) {
          updateObject.deliveryTime = DEFAULT_DELIVERY_TIME;
          updateObject.deliveryTimeCharge = 0;
          updateObject.deliveryTimeSlot = null;
        } else {
          // Get delivery time and charge from selected time slot
          const selectedTimeSlotValue = values.deliveryTimeSlot;
          if (selectedTimeSlotValue) {
            try {
              const timeSlot = JSON.parse(selectedTimeSlotValue);
              updateObject.deliveryTime = `${timeSlot.start}-${timeSlot.end}`;
              updateObject.deliveryTimeCharge = timeSlot.deliverySurcharge || 0;
              updateObject.deliveryTimeSlot = timeSlot; // Store the full timeslot object
            } catch (e) {
              console.error("Error parsing time slot:", e);
              // Fallback to old logic
              if (MORNING_EXTRA_CHARGE_LABELS.includes(values.deliveryLabel)) {
                updateObject.deliveryTimeCharge = MORNING_DELIVERY_CHARGE;
                updateObject.deliveryTime = MORNING_DELIVERY_TIME;
                updateObject.deliveryTimeSlot = null;
              } else {
                updateObject.deliveryTimeCharge = 0;
                updateObject.deliveryTime = DEFAULT_DELIVERY_TIME;
                updateObject.deliveryTimeSlot = null;
              }
            }
          } else if (MORNING_EXTRA_CHARGE_LABELS.includes(values.deliveryLabel)) {
            // Fallback to old logic if no time slot selected
            updateObject.deliveryTimeCharge = MORNING_DELIVERY_CHARGE;
            updateObject.deliveryTime = MORNING_DELIVERY_TIME;
            updateObject.deliveryTimeSlot = null;
          } else {
            updateObject.deliveryTimeCharge = 0;
            updateObject.deliveryTime = DEFAULT_DELIVERY_TIME;
            updateObject.deliveryTimeSlot = null;
          }
        }

        if (deliveryLabelChanged) {
          updateObject.deliveryLabel = values.deliveryLabel;
        }
      }

      if (shoppingCartChanged || shippingCostSwitchChanged || deliveryLabelChanged || deliveryTimeSlotChanged) {
        const customer = customers.find((customer) => customer.id === order.farmorderCustomerId);

        const pricingUpdate = calculateOrderUpdatePricing(
          productsWithToggles,
          customer,
          values.deliveryLabel,
          editAddShippingCost,
          discountCodeData,
          productsToUse,
          order,
          updateObject.deliveryTimeCharge,
          priceLists,
          getCountriesMap(),
        );

        Object.assign(updateObject, pricingUpdate);
      }

      if (orderCommentsSwitchChanged) {
        updateObject.orderCommentsProcessed = values.orderCommentsProcessed === true;
      }

      if (values.internalNotes) {
        if (order.internalNotes && internalNotesChanged) {
          updateObject.internalNotes = {
            ...order.internalNotes,
            text: values.internalNotes,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          };
        } else {
          updateObject.internalNotes = {
            text: values.internalNotes,
            createdAt: dayjs().unix(),
            createdBy: email,
          };
        }
      }

      const newFarmorder = {
        ...order,
        ...updateObject,
        deliveryLabelChanged: !!updateObject.deliveryLabel,
      };

      // Handle additional order logic when delivery date changes
      if (deliveryDateChanged && order.farmorderCustomerId !== JIMMY_LOVES_FOOD_ID) {
        const newDeliveryDate = dayjs(values.deliveryDate).format("YYYY-MM-DD");
        const isCurrentlyAdditionalOrder = !!order.mainOrderId;
        const isCurrentlyMainOrder = !isCurrentlyAdditionalOrder;

        // Find if there's already a main order on the new date for this customer
        const existingOrderOnNewDate = orders.find(
          (o) =>
            o.farmorderCustomerId === order.farmorderCustomerId &&
            o.farmorderDeliveryDate === newDeliveryDate &&
            o.id !== order.id &&
            o.farmorderStatus !== ORDER_STATUS_CANCELED &&
            !o.mainOrderId, // Only look for main orders
        );

        // Scenario C & D: Main order is being moved
        if (isCurrentlyMainOrder) {
          // Scenario D: New date has existing main order
          if (existingOrderOnNewDate) {
            // This order becomes additional
            updateObject.mainOrderId = existingOrderOnNewDate.id;
            updateObject.deliveryLabel = ADDITIONAL_DELIVERY_LABEL;

            // Additional deliveries never have shipping costs
            // Note: pricing was potentially calculated earlier using values.deliveryLabel, so we must override it here.
            updateObject.shippingCostDisabled = true;
            const customer = customers.find((c) => c.id === order.farmorderCustomerId);
            if (customer) {
              const additionalPricingUpdate = calculateOrderUpdatePricing(
                productsWithToggles,
                customer,
                ADDITIONAL_DELIVERY_LABEL,
                false,
                discountCodeData,
                productsToUse,
                order,
                updateObject.deliveryTimeCharge,
                priceLists,
                getCountriesMap(),
              );
              Object.assign(updateObject, additionalPricingUpdate);
            } else {
              updateObject.shippingCost = "0";
            }

            // Update existing main order to include this order
            const existingMainOrderRef = doc(
              firestore,
              FIRESTORE_REGIONS_TABLE,
              COMPANY_COUNTRY_CODE,
              FIRESTORE_ORDERS_TABLE,
              existingOrderOnNewDate.id,
            );
            await updateDoc(existingMainOrderRef, {
              additionalOrderIds: arrayUnion(order.id),
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });

            // Move all additional orders of this order to the new main order
            if (order.additionalOrderIds && order.additionalOrderIds.length > 0) {
              for (const additionalOrderId of order.additionalOrderIds) {
                const additionalOrderRef = doc(
                  firestore,
                  FIRESTORE_REGIONS_TABLE,
                  COMPANY_COUNTRY_CODE,
                  FIRESTORE_ORDERS_TABLE,
                  additionalOrderId,
                );

                await updateDoc(additionalOrderRef, {
                  farmorderDeliveryDate: newDeliveryDate,
                  mainOrderId: existingOrderOnNewDate.id,
                  updatedAt: dayjs().unix(),
                  updatedBy: email,
                });

                // Add to existing main order's additionalOrderIds
                await updateDoc(existingMainOrderRef, {
                  additionalOrderIds: arrayUnion(additionalOrderId),
                  updatedAt: dayjs().unix(),
                  updatedBy: email,
                });
              }
            }

            // Remove additionalOrderIds from this order since it's now additional
            updateObject.additionalOrderIds = deleteField();
          } else {
            // Scenario C: New date has no existing order - move all additional orders

            if (order.additionalOrderIds && order.additionalOrderIds.length > 0) {
              for (const additionalOrderId of order.additionalOrderIds) {
                const additionalOrderRef = doc(
                  firestore,
                  FIRESTORE_REGIONS_TABLE,
                  COMPANY_COUNTRY_CODE,
                  FIRESTORE_ORDERS_TABLE,
                  additionalOrderId,
                );

                await updateDoc(additionalOrderRef, {
                  farmorderDeliveryDate: newDeliveryDate,
                  updatedAt: dayjs().unix(),
                  updatedBy: email,
                });
              }
            }
          }
        }

        // Scenario A & B: Additional order is being moved
        if (isCurrentlyAdditionalOrder) {
          // Remove from old main order's additionalOrderIds
          const oldMainOrderRef = doc(
            firestore,
            FIRESTORE_REGIONS_TABLE,
            COMPANY_COUNTRY_CODE,
            FIRESTORE_ORDERS_TABLE,
            order.mainOrderId,
          );
          await updateDoc(oldMainOrderRef, {
            additionalOrderIds: arrayRemove(order.id),
            updatedAt: dayjs().unix(),
            updatedBy: email,
          });

          // Scenario A: New date has existing main order
          if (existingOrderOnNewDate) {
            updateObject.mainOrderId = existingOrderOnNewDate.id;
            // deliveryLabel stays "ADD"

            // Additional deliveries never have shipping costs
            updateObject.shippingCostDisabled = true;
            const customer = customers.find((c) => c.id === order.farmorderCustomerId);
            if (customer) {
              const additionalPricingUpdate = calculateOrderUpdatePricing(
                productsWithToggles,
                customer,
                ADDITIONAL_DELIVERY_LABEL,
                false,
                discountCodeData,
                productsToUse,
                order,
                updateObject.deliveryTimeCharge,
                priceLists,
                getCountriesMap(),
              );
              Object.assign(updateObject, additionalPricingUpdate);
            } else {
              updateObject.shippingCost = "0";
            }

            // Add to new main order's additionalOrderIds
            const newMainOrderRef = doc(
              firestore,
              FIRESTORE_REGIONS_TABLE,
              COMPANY_COUNTRY_CODE,
              FIRESTORE_ORDERS_TABLE,
              existingOrderOnNewDate.id,
            );
            await updateDoc(newMainOrderRef, {
              additionalOrderIds: arrayUnion(order.id),
              updatedAt: dayjs().unix(),
              updatedBy: email,
            });
          } else {
            // Scenario B: New date has no main order - becomes standalone
            updateObject.mainOrderId = deleteField();

            // Set deliveryLabel to customer's default
            const customer = customers.find((c) => c.id === order.farmorderCustomerId);
            updateObject.deliveryLabel =
              customer?.deliveryLabel || defaultDeliverySettings?.defaultLabel || DEFAULT_DELIVERY_LABEL_FALLBACK;
          }
        }
      }

      const token = await getFirebaseIDToken();

      // HANDLE SOOCOOL - Only attempt if the change involves SooCool labels
      const effectiveDeliveryLabelForSooCool = updateObject?.deliveryLabel || values.deliveryLabel;
      const deliveryLabelChangedForSooCool =
        (order?.deliveryLabel || order?.customer?.deliveryLabel) !== effectiveDeliveryLabelForSooCool;

      const sooCoolResult = await handleSooCoolOrderChanges(
        order,
        effectiveDeliveryLabelForSooCool,
        {
          deliveryLabelChanged: deliveryLabelChangedForSooCool,
          deliveryDateChanged,
          cratesChanged,
          scannedCratesChanged,
          shoppingCartChanged,
          newOrderData: newFarmorder,
        },
        handleSendToSooCool,
        cancelOrderInSooCool,
      );

      operationResults.sooCool = sooCoolResult;

      // Handle special SooCool responses
      if (sooCoolResult.skipDeliveryLabelUpdate) {
        delete updateObject.deliveryLabel;
      }
      if (sooCoolResult.revertCrates) {
        updateObject.crates = originalCrates;
      }
      if (sooCoolResult.revertScannedCrates) {
        updateObject.scannedCrates = order.scannedCrates || [];
      }

      if (!sooCoolResult.success && sooCoolResult.attempted && !sooCoolResult.error?.includes("Time locked")) {
        setSubmitLoading(false);
        showOperationSummary(operationResults);
        return;
      }

      // SAVE TO FIRESTORE & UPDATE STOCK
      try {
        const orderDocRef = doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_ORDERS_TABLE,
          order.id,
        );
        await updateDoc(orderDocRef, { ...updateObject, updatedAt: dayjs().unix(), updatedBy: email });

        operationResults.firestore.success = true;

        // Update packaging stock if scannedCrates changed
        if (scannedCratesChanged) {
          try {
            const stockResult = await updatePackagingStockDelta(order.scannedCrates || [], scannedCrates || [], email);
            if (!stockResult.success) {
              console.error("Failed to update packaging stock:", stockResult.error);
              // Log but don't block the operation
            }
          } catch (error) {
            console.error("Error updating packaging stock:", error);
            // Log but don't block the operation
          }
        }

        // Update orderPicking if order is being picked
        if (orderWasBeingPicked) {
          try {
            const orderUpdates = {};

            // Always update shopping cart if it changed
            if (shoppingCartChanged) {
              // Shopping cart is passed as second parameter
            }

            // Add other changed fields
            if (deliveryLabelChanged) {
              orderUpdates.deliveryLabel = values.deliveryLabel;
            }
            if (cratesChanged) {
              orderUpdates.crates = newCrates;
            }
            if (deliveryDateChanged) {
              orderUpdates.farmorderDeliveryDate = dayjs(values.deliveryDate).format("YYYY-MM-DD");
            }
            if (internalNotesChanged) {
              orderUpdates.internalNotes = updateObject.internalNotes;
            }
            if (orderCommentsSwitchChanged) {
              orderUpdates.orderCommentsProcessed = values.orderCommentsProcessed;
            }

            await updateOrderPickingForEditedOrder(order.id, productsWithToggles, orderUpdates, email);
          } catch (pickingError) {
            console.error("Error updating orderPicking:", pickingError);
            // Don't fail the whole operation, just log it
          }
        }

        if (shoppingCartChanged || deliveryDateChanged) {
          operationResults.stock.attempted = true;
          try {
            await axios.put(stockApiUrl, newFarmorder, {
              headers: { Authorization: token },
            });
            operationResults.stock.success = true;
          } catch (stockError) {
            console.error("Stock API error:", stockError);
            operationResults.stock.error = stockError.message;
          }
        }

        // Send update notification
        operationResults.email = await sendOrderUpdateNotification(
          order,
          {
            shoppingCartChanged,
            deliveryDateChanged,
            newOrderData: newFarmorder,
            updatedOrderProducts: productsWithToggles,
            originalShoppingCart: order.userShoppingCart,
          },
          users,
          customers,
          token,
        );

        const updatedOrder = { ...order, ...updateObject };

        if (printLabelWhenDone) {
          await handlePrint(updatedOrder);
        }

        showOperationSummary(operationResults);
        onCancel();
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        operationResults.firestore.error = firestoreError.message;
        setSubmitLoading(false);
        showOperationSummary(operationResults);
      }
    } catch (validationError) {
      console.error("Form validation error:", validationError);
      setSubmitLoading(false);
    }
  };

  const closeEditModal = () => {
    editOrderForm.resetFields();
    resetModalState();
    onCancel();
  };

  if (!order) return null;

  return (
    <Modal
      title={t("edit_order", { customer: order?.farmorderCustomerName })}
      open={visible}
      onCancel={closeEditModal}
      footer={[
        <Button key="close" onClick={closeEditModal}>
          {t("close")}
        </Button>,
        <Button type="primary" onClick={() => handlePrint()}>
          {t("print")}
        </Button>,
        <Button key="save" type="primary" loading={submitLoading} onClick={() => handleEditOrder(false, false)}>
          {t("save")}
        </Button>,
        <Button key="save-print" type="primary" loading={submitLoading} onClick={() => handleEditOrder(true, false)}>
          {t("save_and_print")}
        </Button>,
      ]}
      width={600}
      centered
    >
      {isAddDelivery && (
        <Alert
          message={`${t("add_delivery")}${mainOrder?.farmorderId || ""}`}
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}
      {deliveryDayWarning && (
        <Alert
          message={deliveryDayWarning}
          type="warning"
          showIcon
          closable
          onClose={() => setDeliveryDayWarning(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={editOrderForm}
        layout={"vertical"}
        onValuesChange={(changedValues, allValues) => {
          if (Object.keys(changedValues).includes("addShippingCost")) {
            setEditAddShippingCost(changedValues.addShippingCost);
          }
        }}
      >
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"deliveryDate"}
              label={t("delivery_date")}
              required={false}
              rules={[{ required: true, message: t("form.pick_value") }]}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "100%" }}>{dayjs(dateValue).format("YYYY-MM-DD")}</div>
                <Popover
                  content={
                    <div>
                      <DatePicker
                        style={{ width: "100%" }}
                        value={tempDeliveryDate}
                        onChange={(date) => setTempDeliveryDate(date)}
                      />
                      <div style={{ marginTop: "8px", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                        <Button type="link" onClick={() => setDatePickerVisible(false)}>
                          {t("cancel")}
                        </Button>
                        <Button type="primary" onClick={() => validateAndUpdateDate(tempDeliveryDate)}>
                          {t("confirm")}
                        </Button>
                      </div>
                    </div>
                  }
                  title={t("form.select_new_delivery_date")}
                  trigger="click"
                  open={datePickerVisible}
                  onOpenChange={(visible) => {
                    setDatePickerVisible(visible);
                    if (visible) {
                      setTempDeliveryDate(editOrderForm.getFieldValue("deliveryDate"));
                    }
                  }}
                >
                  <Button type="link">{t("change")}</Button>
                </Popover>
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              name={"deliveryLabel"}
              label={t("delivery_label")}
              rules={[{ required: true, message: t("form.pick_value") }]}
            >
              <Select
                onChange={(value) => {
                  setDeliveryLabel(value);
                  // Auto-set first time slot when label changes
                  const dbCustomer = customers.find((customer) => customer.id === order?.farmorderCustomerId);
                  if (dbCustomer) {
                    const companyDeliverySettings = getCountryDelivery(dbCustomer.country || COMPANY_COUNTRY_CODE);
                    const labelConfig = companyDeliverySettings?.deliveryLabels?.find(
                      (dl) => dl.deliveryLabel === value,
                    );
                    if (labelConfig?.deliveryTimeSlots?.length > 0) {
                      const firstSlot = labelConfig.deliveryTimeSlots[0];
                      editOrderForm.setFieldValue("deliveryTimeSlot", stringifyTimeSlot(firstSlot));
                      setDeliveryTimeSlot(firstSlot);
                    }
                  }
                  // Validate delivery day when label changes
                  const currentDate = editOrderForm.getFieldValue("deliveryDate");
                  if (currentDate && dbCustomer) {
                    const warning = validateDeliveryDay(currentDate, dbCustomer, value);
                    setDeliveryDayWarning(warning);
                  }
                }}
                value={deliveryLabel}
              >
                {(() => {
                  const dbCustomer = customers.find((customer) => customer.id === order?.farmorderCustomerId);
                  const labels = dbCustomer ? getCountryDeliveryLabels(dbCustomer.country || COMPANY_COUNTRY_CODE) : [];
                  return labels.map((d) => (
                    <Select.Option value={d.code} key={d.code}>
                      {d.label}
                    </Select.Option>
                  ));
                })()}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="deliveryTimeSlot"
              label={t("delivery_time_slot")}
              rules={[
                {
                  required: deliveryLabel !== ADDITIONAL_DELIVERY_LABEL,
                  message: t("form.select_delivery_time_slot"),
                },
              ]}
            >
              <Select
                placeholder={t("select_delivery_time_slot")}
                disabled={!deliveryLabel || !order?.farmorderCustomerId || deliveryLabel === ADDITIONAL_DELIVERY_LABEL}
                onChange={(value) => {
                  try {
                    const slot = JSON.parse(value);
                    setDeliveryTimeSlot(slot);
                  } catch (e) {
                    console.error("Error parsing time slot:", e);
                  }
                }}
                optionLabelProp="label"
              >
                {deliveryLabel &&
                  order?.farmorderCustomerId &&
                  (() => {
                    const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
                    const companyDeliverySettings = getCountryDelivery(dbCustomer?.country || COMPANY_COUNTRY_CODE);
                    return companyDeliverySettings?.deliveryLabels
                      ?.find((dl) => dl.deliveryLabel === deliveryLabel)
                      ?.deliveryTimeSlots?.map((slot, index) => {
                        const label = `${slot.start} - ${slot.end}${
                          slot.deliverySurcharge > 0 ? ` (+€${slot.deliverySurcharge})` : ""
                        }`;
                        return (
                          <Select.Option key={index} value={stringifyTimeSlot(slot)} label={label}>
                            {label}
                          </Select.Option>
                        );
                      });
                  })()}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item name={"addShippingCost"} label={t("enable_shipping_cost")} valuePropName="checked">
              <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} disabled={deliveryLabel === "JLVO"} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name={"internalNotes"} label={t("internal_notes")}>
              <Input.TextArea placeholder={t("internal_order_notes_placeholder")} />
            </Form.Item>
          </Col>
          {!!order && order.farmorderComments && (
            <>
              <Divider style={{ marginTop: 0 }} />
              <Col xs={24}>
                <Form.Item name={"orderComments"} label={t("order_comments")}>
                  <Input.TextArea readOnly />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name={"orderCommentsProcessed"}
                  label={t("notifications.order_comments_processed")}
                  valuePropName="checked"
                >
                  <Switch checkedChildren={t("yes")} unCheckedChildren={t("no")} />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        <Divider style={{ marginTop: 0 }} />

        {/* Crates Section */}
        <div style={{ marginBottom: "16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
            <h4 style={{ margin: 0 }}>{t("crates")}</h4>
            <div>
              <Popover
                content={
                  <div style={{ width: "200px" }}>
                    {CRATES.map((crate, index) => (
                      <div
                        key={crate.barcode}
                        onClick={() => {
                          handleAddCrate(crate.barcode);
                          setAddCrateDropdownVisible(false);
                        }}
                        style={{
                          padding: "8px 12px",
                          cursor: "pointer",
                          borderRadius: "4px",
                          transition: "background-color 0.2s",
                          borderBottom: index < CRATES.length - 1 ? "1px solid #f0f0f0" : "none",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f5f5f5";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {crate.name}
                      </div>
                    ))}
                  </div>
                }
                trigger="click"
                open={addCrateDropdownVisible}
                onOpenChange={(visible) => {
                  setAddCrateDropdownVisible(visible);
                }}
              >
                <Button type="text" icon={<PlusOutlined />} size="small">
                  {t("add")}
                </Button>
              </Popover>
            </div>
          </div>

          <div>
            {scannedCrates.map((scannedCrate, index) => {
              const crate = CRATES.find((c) => c.barcode === scannedCrate.barcode);
              const isLast = index === scannedCrates.length - 1;
              return (
                <div
                  key={scannedCrate.barcode}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                  }}
                >
                  <span>{crate.name}</span>
                  <QuantityControl
                    productId={scannedCrate.barcode}
                    currentQuantity={scannedCrate.quantity}
                    onIncrease={() => {
                      setScannedCrates((prev) =>
                        prev.map((c) => (c.barcode === scannedCrate.barcode ? { ...c, quantity: c.quantity + 1 } : c)),
                      );
                    }}
                    onDecrease={() => {
                      if (scannedCrate.quantity > 0) {
                        setScannedCrates((prev) => {
                          const updated = prev.map((c) =>
                            c.barcode === scannedCrate.barcode ? { ...c, quantity: Math.max(0, c.quantity - 1) } : c,
                          );
                          return updated.filter((c) => c.quantity > 0);
                        });
                      }
                    }}
                    onManualInput={(id, value) => {
                      setScannedCrates((prev) => {
                        const updated = prev.map((c) =>
                          c.barcode === scannedCrate.barcode ? { ...c, quantity: Math.max(0, value) } : c,
                        );
                        return updated.filter((c) => c.quantity > 0);
                      });
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <Divider />
        <h3>{t("products")}</h3>
        <div style={{ marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
          <Switch checked={showAllProducts} onChange={setShowAllProducts} size="small" />
          <span style={{ fontSize: "14px" }}>{t("show_all_products")}</span>
        </div>
        {updatedOrderProducts?.map((product) => {
          const { productId, productName, itemCount } = product;
          const quantityDiff = getQuantityDifference(productId, itemCount);
          const isExceedingStock = productsExceedingStock[productId];
          const existedInOriginalOrder = order.userShoppingCart.some((item) => item.productId === productId);
          const quantityChanged = hasQuantityChanged(productId, itemCount, order);

          return (
            <EditOrderCartItem key={productId} isRemoved={existedInOriginalOrder && itemCount === 0}>
              <div style={{ display: "flex" }}>
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (existedInOriginalOrder) {
                      // Set quantity to 0 for existing products
                      const newProducts = cloneDeep(updatedOrderProducts);
                      const index = newProducts.findIndex((item) => item.productId === productId);
                      if (index > -1) {
                        newProducts[index].itemCount = 0;
                        setUpdatedOrderProducts(newProducts);
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    } else {
                      // Delete newly added products
                      const newProducts = cloneDeep(updatedOrderProducts);
                      const index = newProducts.findIndex((item) => item.productId === productId);
                      if (index > -1) {
                        newProducts.splice(index, 1);
                        setUpdatedOrderProducts(newProducts);
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    }
                  }}
                  style={{ marginRight: "5px" }}
                />
                <div>
                  <h4>{productName}</h4>

                  {/* Price editor */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                    {manualPriceOverrides[productId] !== undefined ? (
                      // Edit mode - show input
                      <>
                        <Input
                          type="number"
                          step="0.01"
                          size="small"
                          style={{ width: "80px" }}
                          prefix="€"
                          value={manualPriceOverrides[productId]}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "" || parseFloat(value) >= 0) {
                              setManualPriceOverrides((prev) => ({
                                ...prev,
                                [productId]: value,
                              }));
                            }
                          }}
                          autoFocus
                          onBlur={() => {
                            if (!manualPriceOverrides[productId]) {
                              setManualPriceOverrides((prev) => {
                                const newOverrides = { ...prev };
                                delete newOverrides[productId];
                                return newOverrides;
                              });
                            } else if (product.isBundle) {
                              const packagingPrice = getBundlePackagingPrice(product);
                              if (parseFloat(manualPriceOverrides[productId]) < packagingPrice) {
                                setManualPriceOverrides((prev) => ({
                                  ...prev,
                                  [productId]: packagingPrice.toFixed(2),
                                }));
                              }
                            }
                          }}
                        />
                        <span style={{ fontSize: "12px", color: "#999", textDecoration: "line-through" }}>
                          €{formatPrice(parseFloat(product.productPrice))}
                        </span>
                        {product.isBundle && getBundlePackagingPrice(product) > 0 && (
                          <span style={{ fontSize: "11px", color: "#888" }}>
                            ({t("packaging")}: €{formatPrice(getBundlePackagingPrice(product))})
                          </span>
                        )}
                        {product.isBundle &&
                          manualPriceOverrides[productId] !== "" &&
                          parseFloat(manualPriceOverrides[productId]) < getBundlePackagingPrice(product) && (
                            <span style={{ fontSize: "11px", color: "#ff4d4f" }}>
                              {t("bundle_price_below_packaging")}
                            </span>
                          )}
                        <Button
                          type="link"
                          size="small"
                          onClick={() => {
                            setManualPriceOverrides((prev) => {
                              const newOverrides = { ...prev };
                              delete newOverrides[productId];
                              return newOverrides;
                            });
                          }}
                          style={{ padding: "0 4px", fontSize: "12px" }}
                        >
                          {t("reset")}
                        </Button>
                      </>
                    ) : (
                      // Display mode - clickable price
                      <span
                        onClick={() => {
                          setManualPriceOverrides((prev) => ({
                            ...prev,
                            [productId]: product.productPrice,
                          }));
                        }}
                        style={{
                          fontSize: "12px",
                          cursor: "pointer",
                        }}
                      >
                        €{formatPrice(parseFloat(product.productPrice))}
                      </span>
                    )}
                  </div>

                  {isExceedingStock && (
                    <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                      {t("exceeds_available_stock")}
                    </p>
                  )}
                  {existedInOriginalOrder && itemCount === 0 && (
                    <p style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>
                      {t("product_will_be_removed")}
                    </p>
                  )}
                </div>
              </div>
              <div>
                {quantityChanged && (
                  <div style={{ marginTop: "8px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <Switch
                      size="small"
                      checked={currentSessionToggles[productId]?.changeRequestedBy === "customer"}
                      onChange={(checked) => {
                        if (checked) {
                          // Directly set customer-initiated
                          setCurrentSessionToggles((prev) => ({
                            ...prev,
                            [productId]: {
                              changeRequestedBy: "customer",
                            },
                          }));
                        } else {
                          // Remove from current session toggles
                          setCurrentSessionToggles((prev) => {
                            const newToggles = { ...prev };
                            delete newToggles[productId];
                            return newToggles;
                          });
                        }
                      }}
                    />
                    <span style={{ fontSize: "12px", color: "#666" }}>{t("customer_requested")}</span>
                  </div>
                )}
              </div>
              <div>
                <QuantityControl
                  key={`${productId}-${order.id}`}
                  productId={productId}
                  currentQuantity={itemCount}
                  onIncrease={() => increaseQuantity(product)}
                  onDecrease={() => decreaseQuantity(product)}
                  onManualInput={(productId, value) => {
                    const newProducts = cloneDeep(updatedOrderProducts);
                    const index = newProducts.findIndex((item) => item.productId === productId);

                    // Check if product existed in original order
                    const existedInOriginalOrder = order.userShoppingCart.some((item) => item.productId === productId);

                    if (value === 0 && !existedInOriginalOrder) {
                      // Remove newly added products when set to 0
                      if (index > -1) {
                        newProducts.splice(index, 1);
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    } else if (index > -1) {
                      // Update existing product (or keep at 0 if it existed in original order)
                      newProducts[index].itemCount = value;

                      // Check stock validation
                      const packingDateId = getEditOrderPackingDateId(editOrderForm.getFieldValue("deliveryDate"));
                      const stockInfo = stockData[packingDateId]?.[productId] || { currentStock: 0 };

                      let originalQuantity = 0;
                      if (order && order.userShoppingCart) {
                        const originalProduct = order.userShoppingCart.find((item) => item.productId === productId);
                        if (originalProduct) {
                          const originalPackingDateId = getEditOrderPackingDateId(order.farmorderDeliveryDate);
                          if (packingDateId === originalPackingDateId) {
                            originalQuantity = originalProduct.itemCount;
                          }
                        }
                      }

                      const effectiveStock = stockInfo.currentStock + originalQuantity;
                      if (value > effectiveStock) {
                        setProductsExceedingStock((prev) => ({
                          ...prev,
                          [productId]: true,
                        }));
                      } else {
                        setProductsExceedingStock((prev) => {
                          const newState = { ...prev };
                          delete newState[productId];
                          return newState;
                        });
                      }
                    }

                    setUpdatedOrderProducts(newProducts);
                  }}
                  additionalDisplay={
                    quantityDiff && (
                      <span
                        style={{
                          marginLeft: "5px",
                          fontSize: "12px",
                          color: quantityDiff > 0 ? "green" : "red",
                          fontWeight: "bold",
                        }}
                      >
                        {quantityDiff}
                      </span>
                    )
                  }
                />
              </div>
            </EditOrderCartItem>
          );
        })}
        <NewProductsWrapper>
          <Select
            style={{ width: "100%" }}
            placeholder={t("new_products")}
            showSearch
            filterOption={(input, option) => {
              const productName = productsToUse.find((product) => product.productId === option.value)?.productName;
              return productName?.toLowerCase().includes(input.toLowerCase());
            }}
            value={editNewProduct}
            onChange={(value) => {
              if (value) {
                const product = productsToUse?.find((item) => item.productId === value);
                increaseQuantity(product);
                setEditNewProduct(null);
              }
            }}
          >
            {[...productsToUse]
              ?.filter((item) => item.productInSeason === true)
              ?.sort((a, b) => a.productName.localeCompare(b.productName))
              ?.filter((item) => {
                // Only filter out products that are in the cart with itemCount > 0
                const productInCart = updatedOrderProducts?.find(
                  (orderProduct) => orderProduct.productId === item.productId,
                );
                return !productInCart || productInCart.itemCount === 0;
              })
              ?.map((product) => {
                const isInCart = !!updatedOrderProducts?.find((item) => item.productId === product.productId);
                const { productId, productName } = product;
                const packingDateId = getEditOrderPackingDateId(editOrderForm.getFieldValue("deliveryDate"));
                const stockCarriedOver = stockCarryOverData[packingDateId] === true;
                const stockInfo = stockData[packingDateId]?.[product.productId] || {
                  currentStock: 0,
                  projectedStock: 0,
                  requestedStock: 0,
                };

                let availableStock;
                if (stockCarriedOver) {
                  availableStock = stockInfo.currentStock;
                } else {
                  availableStock = (stockInfo.projectedStock || 0) - (stockInfo.requestedStock || 0);
                }
                const isInStock = availableStock > 0;

                return (
                  <Select.Option key={productId} value={productId}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span>
                        {productName}
                        {isInStock ? (
                          ` (${t("stock")}: ${availableStock})`
                        ) : (
                          <span style={{ color: "#ff4d4f" }}>{` (${t("stock")}: ${availableStock})`}</span>
                        )}
                      </span>
                      {!isInCart && <PlusOutlined />}
                    </div>
                  </Select.Option>
                );
              })}
          </Select>
        </NewProductsWrapper>
        {editAddShippingCost ? (
          <>
            <PriceRow style={{ marginTop: "1.5rem" }}>
              <h4 style={{ fontWeight: "400" }}>{t("sub_total")}</h4>
              <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(editOrderSubtotal)}</h4>
            </PriceRow>
            {(() => {
              const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
              return (
                dbCustomer?.paymentMethod === PAYMENT_METHOD_INVOICE &&
                dbCustomer?.customerId !== JIMMY_LOVES_FOOD_ID &&
                dbCustomer?.company !== GROWY &&
                !dbCustomer?.priceListId && (
                  <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                    {t("invoice_payment_surcharge")} +{getInvoiceSurchargePercentage(dbCustomer)}%
                  </p>
                )
              );
            })()}
            {order?.customerDiscount > 0 &&
              (() => {
                const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
                return !dbCustomer?.priceListId;
              })() && (
                <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                  {t("customer_discount")} -{order.customerDiscount}%
                </p>
              )}
            <PriceRow>
              <h4 style={{ fontWeight: "400" }}>{t("shipping")}</h4>
              <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(editOrderCalculatedShippingCost)}</h4>
            </PriceRow>
            {(() => {
              // Use deliverySurcharge from selected time slot, fallback to old MORNING_DELIVERY_CHARGE logic
              const surcharge =
                deliveryTimeSlot?.deliverySurcharge ||
                (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0);

              if (surcharge > 0) {
                return (
                  <PriceRow>
                    <h4 style={{ fontWeight: "400" }}>{t("delivery_time_surcharge")}</h4>
                    <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(surcharge)}</h4>
                  </PriceRow>
                );
              }
              return null;
            })()}
            {discountCodeData.applied && (
              <PriceRow>
                <h4 style={{ fontWeight: "400" }}>{getDiscountDisplayText()}</h4>
                <h4 style={{ fontWeight: "400" }}>-{CURRENCY + formatPrice(editOrderDiscountAmount)}</h4>
              </PriceRow>
            )}

            <PriceRow style={{ marginBottom: 0 }}>
              <h4>{t("total")}</h4>
              <h4>
                {CURRENCY +
                  formatPrice(
                    editOrderSubtotal +
                      editOrderCalculatedShippingCost +
                      (deliveryTimeSlot?.deliverySurcharge ||
                        (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0)) -
                      editOrderDiscountAmount,
                  )}
              </h4>
            </PriceRow>
            {editOrderCalculatedShippingCost > 0 && (
              <p>
                {t("order_amount_missing_for_free_delivery", {
                  price: formatPrice(
                    (order?.customer?.deliveryCostThreshold ||
                      defaultDeliverySettings?.deliveryCostThreshold ||
                      FREE_DELIVERY_FROM_PRICE) - editOrderSubtotal,
                  ),
                })}
              </p>
            )}
          </>
        ) : (
          <div style={{ marginTop: "1.5rem" }}>
            <PriceRow style={{ marginTop: "1.5rem" }}>
              <h4 style={{ fontWeight: "400" }}>{t("sub_total")}</h4>
              <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(editOrderSubtotal)}</h4>
            </PriceRow>
            {(() => {
              const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
              return (
                dbCustomer?.paymentMethod === PAYMENT_METHOD_INVOICE &&
                dbCustomer?.customerId !== JIMMY_LOVES_FOOD_ID &&
                !dbCustomer?.priceListId && (
                  <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                    {t("invoice_payment_surcharge")} +{getInvoiceSurchargePercentage(dbCustomer)}%
                  </p>
                )
              );
            })()}
            {order?.customerDiscount > 0 &&
              (() => {
                const dbCustomer = customers.find((customer) => customer.id === order.farmorderCustomerId);
                return !dbCustomer?.priceListId;
              })() && (
                <p style={{ fontSize: "12px", margin: "-10px 0 8px 0", textAlign: "left" }}>
                  {t("customer_discount")} -{order.customerDiscount}%
                </p>
              )}
            {(() => {
              // Use deliverySurcharge from selected time slot, fallback to old MORNING_DELIVERY_CHARGE logic
              const surcharge =
                deliveryTimeSlot?.deliverySurcharge ||
                (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0);

              if (surcharge > 0) {
                return (
                  <PriceRow>
                    <h4 style={{ fontWeight: "400" }}>{t("delivery_time_surcharge")}</h4>
                    <h4 style={{ fontWeight: "400" }}>{CURRENCY + formatPrice(surcharge)}</h4>
                  </PriceRow>
                );
              }
              return null;
            })()}

            {discountCodeData.applied && (
              <PriceRow
                style={{
                  backgroundColor: "rgba(24, 144, 255, 0.08)",
                  padding: "8px 12px",
                  borderRadius: "4px",
                  marginBottom: "8px",
                }}
              >
                <h4 style={{ fontWeight: "400" }}>{getDiscountDisplayText()}</h4>
                <h4 style={{ fontWeight: "400", color: "#52c41a" }}>
                  -{CURRENCY + formatPrice(editOrderDiscountAmount)}
                </h4>
              </PriceRow>
            )}
            <PriceRow style={{ marginBottom: 0 }}>
              <h4>{t("total")}</h4>
              <h4>
                {CURRENCY +
                  formatPrice(
                    editOrderSubtotal +
                      (deliveryTimeSlot?.deliverySurcharge ||
                        (MORNING_EXTRA_CHARGE_LABELS.includes(deliveryLabel) ? MORNING_DELIVERY_CHARGE : 0)) -
                      editOrderDiscountAmount,
                  )}
              </h4>
            </PriceRow>
          </div>
        )}
      </Form>
    </Modal>
  );
};

// Styled Components
const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h4 {
    margin-bottom: 0;
  }
`;

const NewProductsWrapper = styled.div`
  .ant-collapse-content-box {
    padding-inline: 0 !important;
  }
`;

const EditOrderCartItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
  padding: 8px;
  border-radius: 4px;
  background-color: ${(props) => (props.isRemoved ? "rgba(255, 77, 79, 0.1)" : "transparent")};
  transition: background-color 0.3s ease;

  h4 {
    margin: 0;
    font-size: 16px;
    line-height: 1.2;
  }

  p {
    margin: 0;
  }

  .quantity-chooser {
    display: flex;
    align-items: center;
    gap: 4px;
    border: 1px solid rgb(232, 232, 232);
    border-radius: 8px;

    p {
      margin: 0;
      font-size: 14px;
      line-height: 1;
      padding-inline: 6px;
      min-width: 50px;
      text-align: center;
      color: #1a3353;
    }

    .anticon {
      font-size: 12px;
      padding: 8px 6px;
      cursor: pointer;
    }
  }
`;

export default EditOrderModal;
